import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from './ThemeToggle';
import { useSiteSettings } from '@/lib/cms';
import { siteSettingsDefault } from '@/lib/site-settings-default';
import { cn } from '@/lib/utils';
import { CommandPalette, useCommandPalette } from './CommandPalette';

/**
 * Sticky header. Transparent over the hero, gains a subtle hairline border +
 * backdrop blur when scrolled. Mobile uses a full-screen sheet.
 *
 * Nav items come from site_settings.nav.primary, with sensible defaults if
 * the database is empty.
 */
export function Header() {
  const { data: settings } = useSiteSettings();
  const navItems = settings?.nav.primary ?? siteSettingsDefault.nav.primary;
  const ctaLabel = settings?.nav.cta_label ?? siteSettingsDefault.nav.cta_label;
  const ctaHref = settings?.nav.cta_href ?? siteSettingsDefault.nav.cta_href;

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const palette = useCommandPalette();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mobileOpen]);

  // Close mobile menu on every route change.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-all duration-base ease-out-quart',
        scrolled
          ? 'border-b border-border bg-bg/70 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="container-default flex h-14 items-center justify-between sm:h-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 transition-opacity hover:opacity-80"
          aria-label={`${settings?.brand.name ?? 'Vonlof'} home`}
        >
          <img
            src={settings?.brand.logo_url ?? '/logo.png'}
            alt=""
            width={256}
            height={256}
            className="h-7 w-7 rounded-sm sm:h-8 sm:w-8"
            decoding="async"
          />
          <span className="sr-only">{settings?.brand.name ?? 'Vonlof'}</span>
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
          {navItems.map((link) => (
            <NavLink
              key={link.href + link.label}
              to={link.href}
              className={({ isActive }) =>
                cn(
                  'relative rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive ? 'text-gold-500' : 'text-fg-muted hover:text-fg',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={cn(
                      'absolute inset-x-3 -bottom-0.5 h-px bg-gold-500 transition-opacity duration-base',
                      isActive ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => palette.setOpen(true)}
            aria-label="Open command palette"
            className="inline-flex h-10 items-center gap-2 rounded-md border border-border px-2.5 text-xs text-fg-muted transition-colors hover:border-gold-500/40 hover:text-fg sm:px-3 touch-target"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Search</span>
            <kbd className="hidden rounded bg-bg-subtle px-1.5 font-mono text-[10px] text-fg-subtle md:inline">⌘K</kbd>
          </button>
          <ThemeToggle />
          <Button asChild size="sm" className="hidden sm:inline-flex">
            <Link to={ctaHref}>{ctaLabel}</Link>
          </Button>
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-fg touch-target md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        links={navItems}
        ctaLabel={ctaLabel}
        ctaHref={ctaHref}
      />
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </header>
  );
}

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  links: Array<{ label: string; href: string }>;
  ctaLabel: string;
  ctaHref: string;
}

function MobileMenu({ open, onClose, links, ctaLabel, ctaHref }: MobileMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      className="fixed inset-0 z-[60] flex flex-col bg-bg/98 backdrop-blur-md md:hidden"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="container-default flex h-14 items-center justify-between sm:h-16">
        <span className="inline-flex items-center gap-2">
          <img
            src="/logo.png"
            alt=""
            width={256}
            height={256}
            className="h-7 w-7 rounded-sm sm:h-8 sm:w-8"
            decoding="async"
          />
          <span className="sr-only">Vonlof</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-fg touch-target"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav aria-label="Mobile primary" className="container-default flex flex-1 flex-col gap-3 overflow-y-auto py-8">
        {links.map((link, i) => (
          <NavLink
            key={link.href + link.label}
            to={link.href}
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'display-serif text-3xl transition-colors sm:text-4xl',
                isActive ? 'text-gold-500' : 'text-fg hover:text-gold-500',
              )
            }
            style={{ animationDelay: `${i * 60}ms` }}
          >
            {link.label}
          </NavLink>
        ))}
        <div className="mt-auto pt-8">
          <Button asChild size="lg" className="w-full">
            <Link to={ctaHref} onClick={onClose}>
              {ctaLabel} →
            </Link>
          </Button>
          <p className="mt-6 text-center text-xs text-fg-subtle">
            Press <kbd className="rounded bg-bg-subtle px-1.5 font-mono text-[10px]">Esc</kbd> to close
          </p>
        </div>
      </nav>
    </div>
  );
}